import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
} from 'lucide-react';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showNotification = useCallback(
    (message, type = 'error', duration = 5000) => {
      const id = Date.now();
      setNotifications((prev) => [...prev, { id, message, type }]);
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, duration);
    },
    []
  );

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const notify = {
    error: (msg) => showNotification(msg, 'error'),
    success: (msg) => showNotification(msg, 'success'),
    info: (msg) => showNotification(msg, 'info'),
    warning: (msg) => showNotification(msg, 'warning'),
  };

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {notifications.map((n) => (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
              className="pointer-events-auto"
            >
              <Toast
                message={n.message}
                type={n.type}
                onClose={() => removeNotification(n.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

const Toast = ({ message, type, onClose }) => {
  const configs = {
    error: {
      icon: <AlertCircle className="w-5 h-5 text-red-500" />,
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-900',
      shadow: 'shadow-red-200/50',
    },
    success: {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-900',
      shadow: 'shadow-emerald-200/50',
    },
    warning: {
      icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-900',
      shadow: 'shadow-amber-200/50',
    },
    info: {
      icon: <Info className="w-5 h-5 text-blue-500" />,
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      text: 'text-blue-900',
      shadow: 'shadow-blue-200/50',
    },
  };

  const config = configs[type] || configs.info;

  return (
    <div
      className={`
        flex items-center gap-3 px-5 py-4 min-w-[320px] max-w-md 
        rounded-2xl border ${config.border} ${config.bg}
        shadow-[0_10px_30px_-5px] ${config.shadow}
        backdrop-blur-sm bg-opacity-95
      `}
    >
      <div className="shrink-0">{config.icon}</div>
      <p
        className={`flex-1 text-[13px] font-bold ${config.text} leading-tight`}
      >
        {message}
      </p>
      <button
        onClick={onClose}
        className="shrink-0 p-1 hover:bg-black/5 rounded-lg transition-colors"
      >
        <X className="w-4 h-4 opacity-40 hover:opacity-100" />
      </button>
    </div>
  );
};
